/**
 * Runtime config reactor — applies live `/api/config` state to the UI.
 *
 * Subscribed to `onConfigChange` from config.ts (which polls /api/config
 * periodically). On every payload, this module:
 *   1. Greys out / re-enables every payment CTA (Top up / Get eSIM) based
 *      on `paymentsEnabled`
 *   2. Renders the hard-maintenance takeover (full-page "We'll be back")
 *      if `maintenanceHard=true`
 *   3. Renders the sitewide banner with priority:
 *        maintenanceHard → no banner (takeover wins)
 *        !paymentsEnabled → maintenance banner
 *        banner.enabled → promo banner
 *        else → no banner
 *
 * Banner dismiss is keyed by a hash of (variant + text), so editing copy
 * in /admin/settings re-shows a previously-dismissed banner.
 *
 * Originally inline in `main.ts` lines 441-615. Phase 2.3 split 2026-05-12.
 */
import { escapeHtml } from './utils';
import { t, getLocale } from './i18n';
import type { PublicConfig } from './config';
import { Auth } from './auth';

/* ───────────── Payments CTA disable/enable ────────────── */

function paymentsCtaSelector(): string {
  return [
    '[data-action="open-topup"]',
    '[data-action="get-esim"]',
  ].join(',');
}

export function applyPaymentsState(enabled: boolean): void {
  const tooltip = t('maintenance.paymentsOffTooltip');
  document.querySelectorAll<HTMLElement>(paymentsCtaSelector()).forEach((el) => {
    if (enabled) {
      el.removeAttribute('disabled');
      el.removeAttribute('data-payments-off');
      if (el.dataset.origTitle !== undefined) {
        if (el.dataset.origTitle) el.title = el.dataset.origTitle; else el.removeAttribute('title');
        delete el.dataset.origTitle;
      }
    } else {
      el.setAttribute('disabled', '');
      el.setAttribute('data-payments-off', '');
      if (el.dataset.origTitle === undefined) el.dataset.origTitle = el.getAttribute('title') ?? '';
      el.title = tooltip;
    }
  });
}

/* ───────────── Site banner ────────────── */

type BannerVariant = 'maintenance' | 'promo' | 'critical';

const BANNER_ID = 'site-banner';
const DISMISS_KEY_PREFIX = 'roamzy.banner.dismissed:';

function fnv1a(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h.toString(16);
}

/** Render `**foo**` → `<strong>foo</strong>` while HTML-escaping everything
 *  else. Operator-controlled text but never executes arbitrary HTML. */
function bannerHtml(raw: string): string {
  const escaped = escapeHtml(raw);
  return escaped.replace(/\*\*([^*]+?)\*\*/g, '<strong>$1</strong>');
}

/**
 * Locale-independent identity of the current banner campaign: the English
 * copy, falling back to the legacy single-string field. Two users on
 * different languages must derive the SAME dismiss key from it.
 */
function bannerIdentity(c: PublicConfig): string {
  return (c.banner.text?.en ?? '').trim() || c.maintenanceBanner.trim();
}

function localizedBannerText(c: PublicConfig): string {
  const loc = getLocale();
  const map = c.banner.text;
  const fromLocale = map[loc as keyof typeof map];
  return (fromLocale && fromLocale.trim()) || map.en || '';
}

/**
 * @param identity Locale-INDEPENDENT string identifying the banner campaign.
 *   The dismiss key hashes this, never the on-screen text: hashing the
 *   localized text meant switching language produced a different key, so a
 *   banner the user had already closed came back in every other language
 *   (operator-reported 2026-07-27). Editing the copy still re-shows the
 *   banner, which was the original intent — it just keys off the canonical
 *   text rather than the translated one.
 */
function renderSiteBanner(text: string, variant: BannerVariant, visible: boolean, identity = ''): void {
  const existing = document.getElementById(BANNER_ID);
  if (!visible || !text.trim()) {
    existing?.remove();
    return;
  }
  // Hash the variant + visible text. If the operator edits copy, the hash
  // changes and a previously-dismissed banner reappears.
  const hash = fnv1a(`${variant}|${identity || text}`);
  const dismissKey = DISMISS_KEY_PREFIX + hash;
  if (typeof localStorage !== 'undefined' && localStorage.getItem(dismissKey) === '1') {
    existing?.remove();
    return;
  }
  const html = bannerHtml(text);
  // The dismiss key is locale-independent on purpose; what is ON SCREEN is
  // not. Comparing only the campaign hash left the old language up after an
  // in-app language switch (2026-09-30: an English promo on /ru pages).
  const onScreen = fnv1a(`${variant}|${text}`);
  if (existing && existing.dataset.hash === hash && existing.dataset.shown === onScreen) {
    // Same content as already-rendered — no-op (avoids re-flash on every poll).
    return;
  }
  existing?.remove();
  // <aside> + role="status" was flagged by Lighthouse a11y audit
  // (aria-allowed-role) — `<aside>` doesn't permit role="status".
  // Use <div> for non-critical variants, keep <aside> for `alert`
  // (allowed there). 2026-05-09 audit pass.
  const el = document.createElement(variant === 'critical' ? 'aside' : 'div');
  el.id = BANNER_ID;
  el.className = `site-banner site-banner--${variant}`;
  if (variant === 'critical') el.setAttribute('role', 'alert');
  el.dataset.hash = hash;
  el.dataset.shown = onScreen;
  el.innerHTML = `
    <div class="site-banner__text">${html}</div>
    <button class="site-banner__close" type="button" aria-label="Dismiss">×</button>
  `;
  document.body.insertBefore(el, document.body.firstChild);
  const close = el.querySelector<HTMLButtonElement>('.site-banner__close');
  close?.addEventListener('click', () => {
    try { localStorage.setItem(dismissKey, '1'); } catch { /* private mode etc. */ }
    el.remove();
  });
}

/* ───────────── Hard maintenance (full-page takeover) ────────────── */

function renderHardMaintenance(active: boolean): void {
  const id = 'site-maintenance-hard';
  let el = document.getElementById(id);
  const app = document.getElementById('app');
  if (!active) {
    el?.remove();
    if (app && app.style.display === 'none') app.style.display = '';
    return;
  }
  if (app) app.style.display = 'none';
  if (!el) {
    el = document.createElement('section');
    el.id = id;
    el.className = 'site-maintenance-hard';
    el.innerHTML = `
      <div class="container" style="text-align:center; padding:120px 24px;">
        <div class="section__kicker" style="margin-bottom:16px;" data-i18n="maintenance.hardKicker">${escapeHtml(t('maintenance.hardKicker'))}</div>
        <h1 class="dash-title" style="font-style:italic;">${escapeHtml(t('maintenance.hardTitle'))}</h1>
      </div>`;
    document.body.appendChild(el);
  }
}

/* ───────────── Combined apply ────────────── */

/**
 * Reactor entry — called by config poller's onChange. Priority:
 * hard takeover → payments-off banner → promo banner → none.
 */
export function applyConfig(c: PublicConfig): void {
  applyPaymentsState(c.paymentsEnabled);
  renderHardMaintenance(c.maintenanceHard);
  if (c.maintenanceHard) {
    renderSiteBanner('', 'maintenance', false);
    return;
  }
  if (!c.paymentsEnabled) {
    // STOP-mode: prefer per-locale banner text, fall back to legacy single
    // string, fall back to i18n template. A PROMO banner's text is not a
    // maintenance notice: with payments off it showed "…sign in to get it"
    // in the maintenance style and never said payments were paused
    // (2026-09-29). Only a notice-severity banner's copy is reused here.
    const text = (c.banner.severity !== 'promo' ? localizedBannerText(c).trim() : '')
      || c.maintenanceBanner.trim()
      || t('maintenance.bannerFallback');
    renderSiteBanner(text, 'maintenance', true, bannerIdentity(c));
    return;
  }
  if (c.banner.enabled) {
    // Audience (2026-09-29). The trial promo — "…sign in to get it" — was
    // shown to everyone, including people already signed in and people who
    // had used their free eSIM long ago. A guests-only banner (the default for
    // promos) stays hidden until /api/me has answered, then only for guests.
    // main.ts re-applies on every auth change, so signing in or out updates it.
    if (c.banner.audience === 'guests' && (!Auth.isResolved() || Auth.isAuthed())) {
      renderSiteBanner('', c.banner.severity, false);
      return;
    }
    const text = localizedBannerText(c);
    renderSiteBanner(text, c.banner.severity, !!text.trim(), bannerIdentity(c));
    return;
  }
  renderSiteBanner('', 'promo', false);
}
